import { useEffect, useState, Fragment } from "react";
import { api, formatApiError, ksh, API } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";
import { useBusinessInfo } from "@/contexts/BusinessInfoContext";
import { Loader2, Download, ChevronDown, ChevronUp, Save, BarChart3, Package, CheckCircle2, Clock, MessageSquare, Smartphone, Send, UserCircle2, UserPlus, ShieldCheck, History, UserX, UserCheck, Truck, MapPin, Copy, Home as HomeIcon, Trash2, Printer } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import IntegrationsTab from "@/components/admin/IntegrationsTab";
import PromosTab from "@/components/admin/PromosTab";
import OrdersTab from "@/components/admin/OrdersTab";
import DispatchTab from "@/components/admin/DispatchTab";
import OfficePacksTab from "@/components/admin/OfficePacksTab";
import StatsTab from "@/components/admin/StatsTab";
import InboxTab from "@/components/admin/InboxTab";
import PricingTab from "@/components/admin/PricingTab";
import StaffTab from "@/components/admin/StaffTab";
import InformationTab from "@/components/admin/InformationTab";
import NotificationsTab from "@/components/admin/NotificationsTab";
import DataHealthTab from "@/components/admin/DataHealthTab";
import { hasPerm, maskPhone } from "@/components/admin/_shared";

export default function Admin() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin" || user?.role === "admin";
  const [tab, setTab] = useState("orders");
  const [unreadCount, setUnreadCount] = useState(0);

  // Poll the unread-orders counter every 30 s so staff see a red dot the
  // moment a new paid order lands (without spamming the API on every render).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/orders/unread-count");
        if (!cancelled) setUnreadCount(Number(data?.unread || 0));
      } catch { /* silent — network flake or missing perm */ }
    };
    load();
    const timer = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [tab]);  // refresh right after switching tabs — likely just claimed an order

  const tabs = [
    { k: "orders", label: "Orders", show: true },
    { k: "dispatch", label: "Dispatch", show: isSuper || hasPerm(user, "orders.dispatch") },
    { k: "inbox", label: "Inbox", show: true },
    { k: "pricing", label: "Pricing", show: isSuper },
    { k: "staff", label: "Staff", show: isSuper },
    { k: "notifications", label: "Notifications", show: isSuper || hasPerm(user, "notifications.send_test") },
    { k: "integrations", label: "Integrations", show: isSuper },
    { k: "info", label: "Information", show: isSuper },
    { k: "promos", label: "Promos", show: isSuper },
    { k: "office-packs", label: "Office Packs", show: isSuper },
    { k: "stats", label: "Stats", show: isSuper || hasPerm(user, "stats.view") },
    { k: "data-health", label: "Data Health", show: isSuper || hasPerm(user, "stats.view") },
  ].filter((t) => t.show);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 lg:py-16">
      <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#525252] mb-2">
        § {isSuper ? "Super Admin Control Room" : "Staff Operations"}
      </div>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Operations</h1>
        <div className="text-xs uppercase tracking-[0.2em] text-[#525252]">
          Logged in as <span className="font-bold text-[#0A0A0A]">{user?.name}</span>
          <span className="ml-2 px-2 py-1 border border-[#0A0A0A] bg-[#0A0A0A] text-white">{user?.role}</span>
        </div>
      </div>

      <div className="flex gap-1 mb-6 border-b border-[#E5E5E5] overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`admin-tab-${t.k}`}
            className={`relative px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold border-b-2 whitespace-nowrap ${tab === t.k ? "border-[#059669] text-[#0A0A0A]" : "border-transparent text-[#525252] hover:text-[#0A0A0A]"}`}
          >
            {t.label}
            {t.k === "orders" && unreadCount > 0 && (
              <span
                data-testid="orders-unread-badge"
                title={`${unreadCount} paid order${unreadCount === 1 ? "" : "s"} waiting to be claimed`}
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-[#DC2626] text-white text-[10px] mono font-bold rounded-full border-2 border-white shadow-sm animate-pulse"
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "orders" && <OrdersTab user={user} />}
      {tab === "dispatch" && (isSuper || hasPerm(user, "orders.dispatch")) && <DispatchTab user={user} />}
      {tab === "inbox" && <InboxTab user={user} />}
      {tab === "pricing" && isSuper && <PricingTab />}
      {tab === "staff" && isSuper && <StaffTab />}
      {tab === "notifications" && <NotificationsTab user={user} />}
      {tab === "integrations" && isSuper && <IntegrationsTab />}
      {tab === "info" && isSuper && <InformationTab />}
      {tab === "promos" && isSuper && <PromosTab />}
      {tab === "office-packs" && isSuper && <OfficePacksTab />}
      {tab === "stats" && (isSuper || hasPerm(user, "stats.view")) && <StatsTab onGoToIntegrations={() => setTab("integrations")} />}
      {tab === "data-health" && (isSuper || hasPerm(user, "stats.view")) && <DataHealthTab user={user} />}
    </div>
  );
}

// StatsTab extracted → /app/frontend/src/components/admin/StatsTab.jsx


// ---- Inbox: incoming WhatsApp messages, manual reply ----

// InboxTab extracted → /app/frontend/src/components/admin/InboxTab.jsx




// PricingTab extracted → /app/frontend/src/components/admin/PricingTab.jsx


// InformationTab extracted → /app/frontend/src/components/admin/InformationTab.jsx




// NotificationsTab extracted → /app/frontend/src/components/admin/NotificationsTab.jsx


// StaffTab extracted → /app/frontend/src/components/admin/StaffTab.jsx


// ---- Promos tab: super-admin promo / discount codes ----
